import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { CreditCard, Plus, Edit, Users, Check } from 'lucide-react';
import { superAdminApi } from '../../utils/superAdminApi';
import { toast } from 'sonner';

export const PlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    max_users: 5,
    price_monthly_chf: 0,
    price_yearly_chf: 0,
    features: [],
    is_public: true
  });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await superAdminApi.getPlans();
      setPlans(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento piani');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        display_name: plan.display_name,
        max_users: plan.max_users,
        price_monthly_chf: plan.price_monthly_chf,
        price_yearly_chf: plan.price_yearly_chf,
        features: plan.features || [],
        is_public: plan.is_public
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        display_name: '',
        max_users: 5,
        price_monthly_chf: 0,
        price_yearly_chf: 0,
        features: [],
        is_public: true
      });
    }
    setDialogOpen(true);
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPlan) {
        await superAdminApi.updatePlan(editingPlan.id, formData);
        toast.success('Piano aggiornato');
      } else {
        await superAdminApi.createPlan(formData);
        toast.success('Piano creato');
      }
      setDialogOpen(false);
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="plans-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Piani di Abbonamento</h1>
          <p className="text-slate-400">Configura i piani disponibili per le aziende</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              <Plus size={18} className="mr-2" />
              Nuovo Piano
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingPlan ? 'Modifica Piano' : 'Nuovo Piano'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Codice (es. PRO) *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value.toUpperCase()})}
                    required
                    className="bg-slate-700 border-slate-600"
                    disabled={!!editingPlan}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Nome Visualizzato *</Label>
                  <Input
                    value={formData.display_name}
                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                    required
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Max Utenti (-1 = illimitati)</Label>
                <Input
                  type="number"
                  value={formData.max_users}
                  onChange={(e) => setFormData({...formData, max_users: parseInt(e.target.value)})}
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Prezzo Mensile (CHF)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_monthly_chf}
                    onChange={(e) => setFormData({...formData, price_monthly_chf: parseFloat(e.target.value)})}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Prezzo Annuale (CHF)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_yearly_chf}
                    onChange={(e) => setFormData({...formData, price_yearly_chf: parseFloat(e.target.value)})}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Funzionalità</Label>
                <div className="flex gap-2">
                  <Input
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    placeholder="Aggiungi funzionalità..."
                    className="bg-slate-700 border-slate-600"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                  />
                  <Button type="button" onClick={handleAddFeature} variant="outline" className="border-slate-600">
                    <Plus size={16} />
                  </Button>
                </div>
                {formData.features.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex items-center justify-between p-2 rounded bg-slate-700/50">
                        <span className="text-sm text-slate-300">{feature}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFeature(index)}
                          className="text-slate-400 hover:text-red-400"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                <div>
                  <Label className="text-slate-300">Disponibile Pubblicamente</Label>
                  <p className="text-xs text-slate-400">Se disattivato, solo assegnabile manualmente</p>
                </div>
                <Switch
                  checked={formData.is_public}
                  onCheckedChange={(v) => setFormData({...formData, is_public: v})}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                  {editingPlan ? 'Salva Modifiche' : 'Crea Piano'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <Card 
            key={plan.id} 
            className={`bg-slate-800 border-slate-700 relative ${
              plan.name === 'PRO' ? 'ring-2 ring-amber-500' : ''
            }`}
          >
            {plan.name === 'PRO' && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500 text-slate-900">
                  POPOLARE
                </span>
              </div>
            )}
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center mb-2">
                <CreditCard className="w-6 h-6 text-amber-500" />
              </div>
              <CardTitle className="text-white">{plan.display_name}</CardTitle>
              <p className="text-xs text-slate-400 font-mono">{plan.name}</p>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <div>
                <p className="text-3xl font-bold text-white">
                  CHF {plan.price_monthly_chf}
                  <span className="text-sm font-normal text-slate-400">/mese</span>
                </p>
                <p className="text-sm text-slate-400">
                  o CHF {plan.price_yearly_chf}/anno
                </p>
              </div>

              <div className="flex items-center justify-center gap-2 text-slate-300">
                <Users size={16} />
                <span>
                  {plan.max_users < 0 ? 'Utenti illimitati' : `Fino a ${plan.max_users} utenti`}
                </span>
              </div>

              {plan.features && plan.features.length > 0 && (
                <div className="text-left space-y-2 pt-2 border-t border-slate-700">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Check size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-300">{feature}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-2">
                {!plan.is_public && (
                  <span className="inline-block px-2 py-1 rounded text-xs bg-slate-700 text-slate-400 mb-2">
                    Solo manuale
                  </span>
                )}
                <Button 
                  variant="outline" 
                  className="w-full border-slate-600 hover:bg-slate-700"
                  onClick={() => handleOpenDialog(plan)}
                >
                  <Edit size={16} className="mr-2" />
                  Modifica
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
